import React, { useMemo } from "react";
import { getTransform } from "../../../../../utils/getTransform";
import {
  aspectFromSize,
  thumbSizeFromAspect,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import { useLiveSourceCardStats } from "../../../hooks/useLiveSourceCardStats";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import "../../../layout/resize/Editor/ResizeModeCards.css";
import "../../../shared/previewBg/ToolPrepPreviewBgCards.css";
import "../../../shared/ToolPrepSourceCards.css";
import ToolPrepUploadDropzone from "../../../shared/Popup/ToolPrepUploadDropzone";
import {
  ToolPrepOriginalOverlays,
} from "../../../shared/ToolPrepSourceIdentity";
import ToolPrepRemoveBgBlock from "../../../shared/Popup/ToolPrepRemoveBgBlock";

const FALLBACK_ASPECT = 3 / 4;
/** Same thumb size as Resize Fit/Fill */
const THUMB_WIDTH_PX = 140;

/**
 * - No image: single full-width upload (150px)
 * - With image: Original | Preview (Replace hover + Remove X on Original)
 *   → optional Remove Background
 */
const TransformPresetCards = ({
  mode = "rotate",
  rotation = 0,
  flipH = false,
  flipV = false,
  selectedKey = "original",
  onSelect = () => {},
  originalImageUrl = null,
  cutoutUrl = null,
  removeBgActive = false,
  cutoutPending = false,
  onUploadClick = null,
  onRemove = null,
  uploadProgress = null,
  sourceName = null,
  sourceSizeLabel = null,
  actionsDisabled = false,
  /**
   * Optional Remove Background controls shown under Original | Preview.
   * @type {null | { active?: boolean, onToggle?: Function, disabled?: boolean, locked?: boolean, bio?: string, error?: string|null, label?: string }}
   */
  removeBgControls = null,
}) => {
  const useCutoutOnAction = Boolean(removeBgActive && cutoutUrl);
  const measureUrl = originalImageUrl || cutoutUrl;
  const naturalSize = useImageNaturalSize(measureUrl);

  const aspect = useMemo(
    () =>
      aspectFromSize(naturalSize.width, naturalSize.height, FALLBACK_ASPECT),
    [naturalSize]
  );
  const thumbSize = useMemo(
    () => thumbSizeFromAspect(aspect, THUMB_WIDTH_PX, FALLBACK_ASPECT),
    [aspect]
  );
  const fit = useMemo(
    () =>
      getTransform(
        naturalSize.width,
        naturalSize.height,
        thumbSize.width,
        thumbSize.height,
        "fit"
      ),
    [naturalSize, thumbSize]
  );

  const actionKey = mode === "flip" ? "flip" : "rotate";
  const angle = Number(rotation) || 0;
  const flipHActive = Boolean(flipH);
  const flipVActive = Boolean(flipV);

  const actionThumbCss =
    mode === "flip"
      ? [`scaleX(${flipHActive ? -1 : 1})`, `scaleY(${flipVActive ? -1 : 1})`].join(
          " "
        )
      : `rotate(${angle === 360 ? 0 : angle}deg)`;

  const thumbStyle = {
    width: thumbSize.width,
    height: thumbSize.height,
    aspectRatio: `${thumbSize.aspect}`,
  };

  const actionSrc = useCutoutOnAction ? cutoutUrl : originalImageUrl;
  const actionLoading = removeBgActive && (!cutoutUrl || cutoutPending);

  const { originalSizeFootnote, previewSizeFootnote } = useLiveSourceCardStats({
    originalImageUrl,
    previewImageUrl: actionSrc || originalImageUrl,
    originalSizeLabel: sourceSizeLabel,
    enabled: Boolean(originalImageUrl) && uploadProgress == null,
  });

  const renderThumbImage = (src, cssTransform) => {
    if (!src) return null;
    const hasTransform = fit.width > 0;
    return (
      <img
        src={src}
        alt=""
        className={
          hasTransform
            ? "resize-mode-thumb__img"
            : "resize-mode-thumb__img resize-mode-thumb__img--fallback"
        }
        style={
          hasTransform
            ? {
                width: fit.width,
                height: fit.height,
                left: fit.x,
                top: fit.y,
                transform: cssTransform || undefined,
                transformOrigin: "center center",
              }
            : {
                transform: cssTransform || undefined,
                transformOrigin: "center center",
              }
        }
        draggable={false}
      />
    );
  };

  if (uploadProgress != null) {
    return (
      <div className="tool-prep-source-cards tool-prep-source-cards--empty">
        <ToolPrepUploadDropzone kind="image" progress={uploadProgress} />
      </div>
    );
  }

  if (!originalImageUrl) {
    return (
      <div className="tool-prep-source-cards tool-prep-source-cards--empty">
        <ToolPrepUploadDropzone
          kind="image"
          onClick={() => onUploadClick?.()}
        />
      </div>
    );
  }

  return (
    <div className="tool-prep-source-cards tool-prep-source-cards--filled">
      <div className="resize-mode-grid tool-prep-source-cards__pair">
        <div
          className={`resize-mode-card tool-prep-source-cards__card${
            selectedKey === "original" ? " resize-mode-card--active" : ""
          }`}
        >
          <div className="tool-prep-source-cards__frame-wrap">
            <button
              type="button"
              className="tool-prep-source-cards__hit"
              onClick={() => onSelect("original")}
              aria-pressed={selectedKey === "original"}
              aria-label="Show original"
            >
              <div className="resize-mode-frame">
                <div className="resize-mode-thumb" style={thumbStyle}>
                  {renderThumbImage(originalImageUrl, null)}
                </div>
              </div>
            </button>
            <ToolPrepOriginalOverlays
              onReplace={onUploadClick}
              onRemove={onRemove}
              disabled={actionsDisabled}
            />
          </div>
          <button
            type="button"
            className="tool-prep-source-cards__label-btn"
            onClick={() => onSelect("original")}
          >
            <span className="resize-mode-label">Original</span>
          </button>
          {originalSizeFootnote ? (
            <span className="tool-prep-source-cards__card-stat">
              {originalSizeFootnote}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className={`resize-mode-card${
            selectedKey === actionKey ? " resize-mode-card--active" : ""
          }`}
          onClick={() => onSelect(actionKey)}
          aria-pressed={selectedKey === actionKey}
        >
          <div className="resize-mode-frame">
            <div
              className={`resize-mode-thumb${
                useCutoutOnAction ? " tpp-bg-thumb--transparent" : ""
              }`}
              style={thumbStyle}
            >
              {actionLoading ? (
                <div className="tpp-bg-thumb__loader">
                  <RoundLoader size="sm" />
                </div>
              ) : actionSrc ? (
                renderThumbImage(actionSrc, actionThumbCss)
              ) : (
                <div className="tool-prep-source-cards__empty tool-prep-source-cards__empty--muted">
                  <span className="tool-prep-source-cards__empty-text">
                    Preview
                  </span>
                </div>
              )}
            </div>
          </div>
          <span className="resize-mode-label">Preview</span>
          {previewSizeFootnote ? (
            <span className="tool-prep-source-cards__card-stat">
              {previewSizeFootnote}
            </span>
          ) : null}
        </button>
      </div>

      {removeBgControls ? (
        <ToolPrepRemoveBgBlock
          inScroll
          active={Boolean(removeBgControls.active)}
          onToggle={removeBgControls.onToggle}
          disabled={Boolean(removeBgControls.disabled)}
          locked={Boolean(removeBgControls.locked)}
          bio={removeBgControls.bio || ""}
          error={removeBgControls.error || null}
          label={removeBgControls.label || "Remove Background"}
        />
      ) : null}
    </div>
  );
};

export default TransformPresetCards;
